export const PERSISTED_AMOUNT_DECIMALS = 6;

export const AMOUNT_DECIMALS_ERROR =
  'El monto admite como máximo 6 decimales';

/** Recorta el texto del input a 6 decimales. 6.2 sigue siendo "6.2". */
export const restrictAmountText = (
  raw: string
): { text: string; exceeded: boolean } => {
  const cleaned = String(raw ?? '')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*?)\..*/g, '$1');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return { text: cleaned, exceeded: false };
  const fraction = cleaned.slice(dot + 1);
  if (fraction.length <= PERSISTED_AMOUNT_DECIMALS) {
    return { text: cleaned, exceeded: false };
  }
  return {
    text: `${cleaned.slice(0, dot)}.${fraction.slice(0, PERSISTED_AMOUNT_DECIMALS)}`,
    exceeded: true,
  };
};

export type MoneyCurrency = 'S' | 'D';

export type MoneyPair = {
  soles: number;
  dollars: number;
};

/** Céntimos. Única forma de persistir un monto. */
export const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * El número ya está guardado a céntimos (83.47), no es el cociente crudo (81.111…).
 * El margen es ruido de float, no una tolerancia de negocio.
 */
export const isRoundedMoney = (value: number): boolean => {
  if (!Number.isFinite(value)) return false;
  return Math.abs(value - roundMoney(value)) < 1e-8;
};

/**
 * Una sola conversión, desde la moneda en que se capturó el monto.
 * La otra moneda es derivada y no se usa para reconstruir el origen.
 */
export const moneyPair = (
  amount: number,
  currency: MoneyCurrency,
  tipoCambio: number
): MoneyPair => {
  const source = roundMoney(amount);
  const tc = Number(tipoCambio);
  const hasTc = Number.isFinite(tc) && tc > 0;
  if (currency === 'S') {
    return {
      soles: source,
      dollars: hasTc ? roundMoney(source / tc) : 0,
    };
  }
  return {
    dollars: source,
    soles: hasTc ? roundMoney(source * tc) : 0,
  };
};

/**
 * Soles de un par ya guardado.
 * Si el dólar no está en céntimos, es tarifa / TC: round(dólar × TC, 2) es el
 * soles exacto para cualquier tipo de cambio y cualquier monto a céntimos.
 * Si el dólar ya está en céntimos, es el derivado: se respeta el soles capturado.
 */
export const resolveSoles = (
  dollars: number | null | undefined,
  soles: number | null | undefined,
  tipoCambio: number
): number => {
  const usd = Number(dollars);
  const pen = Number(soles);
  const tc = Number(tipoCambio);
  const hasUsd = Number.isFinite(usd) && usd > 0;
  const hasPen = Number.isFinite(pen) && pen !== 0;
  const hasTc = Number.isFinite(tc) && tc > 0;

  if (hasUsd && hasTc && !isRoundedMoney(usd)) {
    return roundMoney(usd * tc);
  }
  if (hasPen) return roundMoney(pen);
  if (hasUsd && hasTc) return roundMoney(usd * tc);
  return 0;
};

/**
 * Texto de UI: como máximo 2 decimales, sin redondear.
 * 81.119 → "81.11", 291.992 → "291.99", 292 → "292.00".
 * `toFixed(12)` solo estabiliza el binario; el corte es en el 2.º decimal.
 */
export const formatMoneyDisplay = (
  value: number | string | null | undefined
): string => {
  if (value == null || value === '') return '0.00';
  const raw = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(raw)) return '0.00';
  const negative = raw < 0;
  const [wholeRaw, fracRaw = ''] = Math.abs(raw).toFixed(12).split('.');
  const whole = wholeRaw.replace(/^0+(?=\d)/, '');
  const cents = `${fracRaw}00`.slice(0, 2);
  const body = `${whole}.${cents}`;
  if (negative && body !== '0.00') return `-${body}`;
  return body;
};
